import React, { useState } from 'react';
import classNames from 'classnames';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import uuidv4 from '../../utils/uuidv4';
/**
 * Bronson Card component.
 *
 * @see https://bronson.vwfs.io/default/components/detail/bronson-cards.html
 *
 * @constructor
 */
export function Card({ additionalInfo, ariaLabel, buttons, children, className, contentLarge, contentShort, defaultExpanded, disabled, error, expandable, expandableButtonAriaLabel = 'Details for Card', expandableTestId, footer, footerContent, icon, imageAlt, imageProps, imageSrc, inactive, link, mediaElement, responsiveCollapse, scrollable, small, subtitle, subtitleClassName, success, title, titleClassName, titleLevel = 4, warning, testId, ...otherProps }) {
    /**
     * Track the expanded state of the {@link Card}.
     */
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [responsiveCollapsed, setResponsiveCollapsed] = useState(true);
    const bodyId = uuidv4('bron-card-body-');
    const expandablePanelId = uuidv4('bron-card-expandable-panel-');
    const customTagClassNameList = classNames('c-card', {
        'c-card--image': imageSrc || imageProps,
        'c-card--button-group': buttons && buttons.length > 0,
        'c-card--states': success || warning || error,
        'c-card--states-success': success,
        'c-card--states-warning': warning,
        'c-card--states-error': error,
        'c-card--inactive': inactive,
        'c-card--scrollable': scrollable,
        'c-card--expandable': expandable,
        'c-card--responsive-collapse': responsiveCollapse,
        'c-card--small': small,
    }, className).trim();
    const expandableClassNameList = classNames('c-card__expandable', {
        'is-open': expanded,
    }).trim();
    const expandableTriggerClassNameList = classNames('c-btn--card', 'c-card__expandable-btn', 'js-card-expandable__trigger' // Used in Bronson CSS.
    ).trim();
    const bodyClassNameList = classNames('c-card__body', {
        'js-is-ready': responsiveCollapse, // Used in Bronson CSS.
        'is-collapsed': responsiveCollapse && responsiveCollapsed,
    }).trim();
    const expandablePanelStyle = expanded
        ? {}
        : {
            height: '0px',
            overflowY: 'hidden',
            visibility: 'hidden',
        };
    function renderIfIcon() {
        if (icon) {
            return (React.createElement("div", { className: "c-card__icon" },
                React.createElement(Icon, { name: icon })));
        }
        return null;
    }
    function renderIfImage() {
        if (imageSrc || imageProps) {
            return (React.createElement("div", { className: "c-card__image" },
                React.createElement("img", { src: imageSrc, alt: imageAlt, ...imageProps })));
        }
        return null;
    }
    const renderIfMedia = () => {
        if (mediaElement) {
            return React.createElement("div", { className: "c-card__image" }, mediaElement);
        }
        return null;
    };
    const subtitleClassNameList = classNames('c-card__subtitle', subtitleClassName).trim();
    function renderIfSubtitle() {
        if (subtitle) {
            return React.createElement("p", { className: subtitleClassNameList }, subtitle);
        }
        return null;
    }
    function renderIfResponsiveCollapseButton() {
        if (responsiveCollapse) {
            return (React.createElement("button", { className: "c-card--responsive-collapse__toggle", type: "button", "aria-label": "Toggle Card", "aria-expanded": responsiveCollapsed ? 'false' : 'true', "aria-controls": bodyId, disabled: inactive ? true : undefined, onClick: () => {
                    setResponsiveCollapsed(!responsiveCollapsed);
                } }));
        }
        return null;
    }
    const titleClassNameList = classNames('c-card__title', titleClassName).trim();
    function renderTitle() {
        const HeadingElement = `h${titleLevel}`;
        return (React.createElement("header", { className: "c-card__header" },
            expandable || responsiveCollapse ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "c-card__header-items" },
                    React.createElement(HeadingElement, { className: titleClassNameList }, title),
                    expandable && contentShort,
                    renderIfResponsiveCollapseButton()))) : (React.createElement(React.Fragment, null,
                React.createElement(HeadingElement, { className: titleClassNameList }, title))),
            renderIfSubtitle()));
    }
    function renderIfButtons() {
        if (buttons) {
            return Array.isArray(buttons) ? React.createElement(ButtonContainer, { center: true }, buttons) : buttons;
        }
        return null;
    }
    function renderIfLink() {
        if (link) {
            return (React.createElement("a", { className: "c-btn c-btn--link c-btn--small", href: inactive ? undefined : link.href, role: inactive ? 'link' : undefined, "aria-disabled": inactive ?? undefined },
                React.createElement("span", { className: "c-btn__text" }, link.label),
                React.createElement("i", { className: "c-btn__icon c-icon c-icon--[semantic-forward]", "aria-hidden": "true" })));
        }
        return null;
    }
    function renderIfContentLarge() {
        if (contentLarge) {
            return React.createElement(React.Fragment, null, contentLarge);
        }
        return null;
    }
    function renderIfIsExpandable() {
        /**
         * Render the expandable only if there is also content to reveal.
         */
        if (expandable && contentLarge) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: expandableClassNameList, "aria-disabled": disabled && 'true' },
                    React.createElement(Button, { "aria-controls": expandablePanelId, "aria-expanded": expanded ? 'true' : 'false', ariaLabel: expandableButtonAriaLabel, className: expandableTriggerClassNameList, "data-testid": expandableTestId, icon: "semantic-expand", iconClassName: "c-btn--card-icon", type: "button", disabled: inactive ? true : undefined, onClick: () => {
                            setExpanded(!expanded);
                        } }),
                    React.createElement("div", { className: "c-card__expandable-panel", style: expandablePanelStyle, "aria-expanded": expanded ? 'true' : 'false', "aria-hidden": expanded ? 'false' : 'true', 
                        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
                        inert: expanded ? null : '', id: expandablePanelId }, renderIfContentLarge()))));
        }
        return null;
    }
    function renderIfAdditionalInfo() {
        if (additionalInfo) {
            return React.createElement("div", { className: "u-text-center" }, additionalInfo);
        }
        return null;
    }
    function renderIfFooter() {
        if (footer) {
            return (React.createElement("footer", { className: "c-card__footer" },
                renderIfButtons(),
                renderIfLink(),
                footerContent));
        }
        return null;
    }
    /**
     * Render the card body element.
     */
    function renderBody() {
        const expandableContent = renderIfIsExpandable();
        /**
         * Check whether {@link children} and/or {@link expandable}
         * with {@link contentLarge} is set. Please note that checking
         * for the content of the expandable is as {@link renderIfExpandable}
         * already checks for its content.
         */
        const hasContent = !!(children || expandableContent);
        if (hasContent) {
            return (React.createElement("div", { className: bodyClassNameList, id: responsiveCollapse ? bodyId : undefined },
                children,
                expandableContent));
        }
        return null;
    }
    return (React.createElement("div", { "aria-label": ariaLabel, role: "group", ...otherProps, "data-testid": testId, className: customTagClassNameList },
        renderIfIcon(),
        renderIfImage(),
        renderIfMedia(),
        renderTitle(),
        renderBody(),
        renderIfAdditionalInfo(),
        renderIfFooter()));
}
